<template>
  <div :class="$style.container">
    <SettingsPagePanel v-if="result">
      <SettingsPagePanelHeader>
        <template #title>メンバー詳細</template>
        <template #right>
          <RouterLink
            :to="{
              name: 'memberManagementSettingEdit',
              params: {
                memberManagementSettingId: result.staff.guid,
                entranceGUID: entranceGUID,
              },
            }"
          >
            <FlatButton>編集</FlatButton>
          </RouterLink>
        </template>
      </SettingsPagePanelHeader>
      <SettingsTable :class="$style.table">
        <SettingsTableRow name="メールアドレス">
          <p :class="$style.text">{{ result.staff.email }}</p>
        </SettingsTableRow>
        <SettingsTableRow name="名前">
          <p v-if="result.staff.name" :class="$style.text">
            {{ result.staff.name }}
          </p>
        </SettingsTableRow>
        <SettingsTableRow name="権限">
          <p :class="$style.text">
            {{ roleName }}
          </p>
        </SettingsTableRow>
      </SettingsTable>
      <div
        :class="$style.delete"
        v-if="result.planner && result.planner.email !== result.staff.email"
      >
        <FlatButton
          variant="caution"
          size="medium"
          @click="deleteMember"
          :disabled="deleting"
        >
          メンバーを削除
        </FlatButton>
      </div>
    </SettingsPagePanel>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import SettingsTable from '@/pages/SettingsPage/SettingsTable/SettingsTable.vue'
import SettingsTableRow from '../SettingsTable/SettingsTableRow.vue'
import { useRoute, useRouter } from 'vue-router'

import { useAlert } from '@/libs/alertPortal'
import { captureException } from '@sentry/vue'

import FlatButton from '@/components/atoms/FlatButton.vue'

import SettingsPagePanel from '../SettingsPagePanel.vue'
import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'
import {
  useDeleteStaffMutation,
  useSettingsPageMemberManagementDetailViewQuery,
} from './SettingsPageMemberManagementDetailView.generated'

const route = useRoute('memberManagementSettingDetail')

const staffGUID = route.params.memberManagementSettingId
const entranceGUID = route.params.entranceGUID

const router = useRouter()
const alert = useAlert()

const { result, onError } = useSettingsPageMemberManagementDetailViewQuery(
  {
    staffGUID,
  },
  {
    fetchPolicy: 'no-cache',
  }
)

onError((error): void => {
  if (
    error.graphQLErrors.some(
      ({ extensions }) => extensions && extensions.code === 'PERMISSION_DENIED'
    )
  ) {
    router.replace({ name: 'errorNotFound' })
    return
  }

  alert.error(
    '読込中にエラーが発生しました。お手数ですが再度読み込みをお願いします。'
  )

  captureException(error)
})

const roleName = computed(() => {
  switch (result.value?.staff.role) {
    case 'ENTRANCE_MANAGER':
      return 'アカウント管理者'
    case 'MEMBER':
      return '一般'
    case 'BRANCH_MANAGER':
      return '支社管理者'
    case 'CORPORATION_ADMIN':
      return '企業管理者'
    default:
      return ''
  }
})

const deleteStaffMutation = useDeleteStaffMutation()

const deleting = ref(false)

const deleteMember = async () => {
  if (result.value == undefined) {
    return
  }

  if (staffGUID === result.value.viewer.staff.guid) {
    alert.error('自分自身を削除することはできません。')
    return
  }

  const confirmResult = window.confirm(
    '本当にこのメンバーを削除しますか？この操作は取り消せません。'
  )
  if (!confirmResult) {
    return
  }

  deleting.value = true

  try {
    await deleteStaffMutation.mutate({
      input: {
        guid: staffGUID,
      },
    })

    alert.success('メンバーを削除しました。')

    // eslint-disable-next-line fp/no-mutating-methods
    router.push({
      name: 'memberManagementSetting',
    })
  } catch (e) {
    captureException(e)
    alert.error(
      'エラーが発生しました。再度お試しいただくか、サポートにお問い合わせください。'
    )
  } finally {
    deleting.value = false
  }
}
</script>

<style lang="scss" module>
.container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.delete {
  text-align: center;
  margin-top: 40px;
}
</style>
